import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getGroups, getCostEntries, getGroupBudget, createOrUpdateGroupBudget } from '../../../services/supabaseService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const BudgetAnalysisPanel = () => {
  const { currentLanguage, t } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [budget, setBudget] = useState(null);
  const [actualCosts, setActualCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({
    totalBudget: '',
    accommodationBudget: '',
    transportBudget: '',
    mealsBudget: '',
    guidesBudget: '',
    visasBudget: '',
    otherBudget: ''
  });

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadBudgetData();
    }
  }, [selectedGroup]);

  const loadGroups = async () => {
    try {
      const groupsData = await getGroups();
      setGroups(groupsData || []);
      if (groupsData?.length > 0) {
        setSelectedGroup(groupsData?.[0]?.id);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      const [budgetData, costsData] = await Promise.all([
        getGroupBudget(selectedGroup),
        getCostEntries(selectedGroup)
      ]);

      setBudget(budgetData);
      setActualCosts(costsData?.filter(cost => cost?.approvalStatus === 'approved') || []);
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupOptions = groups?.map(group => ({
    value: group?.id,
    label: `${group?.name} (${group?.type})`
  }));

  const calculateCategoryTotals = () => {
    const categories = ['accommodation', 'transport', 'meals', 'guides', 'visas', 'other'];
    return categories?.map(category => {
      const budgetKey = `${category}Budget`;
      const budgetAmount = budget?.[budgetKey] || 0;
      const actualAmount = actualCosts
        ?.filter(cost => cost?.category === category)
        ?.reduce((sum, cost) => sum + parseFloat(cost?.amount || 0), 0);
      
      return {
        category,
        budget: parseFloat(budgetAmount),
        actual: actualAmount,
        variance: parseFloat(budgetAmount) - actualAmount,
        percentage: budgetAmount > 0 ? (actualAmount / parseFloat(budgetAmount)) * 100 : 0
      };
    });
  };

  const categoryData = budget ? calculateCategoryTotals() : [];

  const totalBudget = budget?.totalBudget || 0;
  const totalActual = actualCosts?.reduce((sum, cost) => sum + parseFloat(cost?.amount || 0), 0);
  const totalVariance = parseFloat(totalBudget) - totalActual;
  const totalPercentage = totalBudget > 0 ? (totalActual / parseFloat(totalBudget)) * 100 : 0;

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2
      })?.format(amount);
    }
  };

  const categoryLabels = {
    accommodation: t('financial.accommodation'),
    transport: t('financial.transport'),
    meals: t('financial.meals'),
    guides: t('financial.guides'),
    visas: t('financial.visasCost'),
    other: t('financial.other')
  };

  const handleSaveBudget = async () => {
    try {
      await createOrUpdateGroupBudget(selectedGroup, budgetForm);
      alert(t('financial.successBudgetSaved'));
      setShowBudgetModal(false);
      loadBudgetData();
    } catch (error) {
      console.error('Error saving budget:', error);
      alert(t('financial.errorSaving'));
    }
  };

  const openBudgetModal = () => {
    if (budget) {
      setBudgetForm({
        totalBudget: budget?.totalBudget || '',
        accommodationBudget: budget?.accommodationBudget || '',
        transportBudget: budget?.transportBudget || '',
        mealsBudget: budget?.mealsBudget || '',
        guidesBudget: budget?.guidesBudget || '',
        visasBudget: budget?.visasBudget || '',
        otherBudget: budget?.otherBudget || ''
      });
    }
    setShowBudgetModal(true);
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-success';
    if (variance < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const getPercentageColor = (percentage) => {
    if (percentage <= 80) return '#10b981'; // success
    if (percentage <= 100) return '#f59e0b'; // warning
    return '#ef4444'; // error
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground">
            {t('financial.budgetAnalysis')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('financial.budgetVsActual')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Select
            options={groupOptions}
            value={selectedGroup}
            onChange={setSelectedGroup}
            className="w-full sm:w-64"
          />
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            iconPosition="left"
            onClick={openBudgetModal}
            disabled={!selectedGroup}
            className="w-full sm:w-auto"
          >
            {budget ? t('financial.editBudget') : t('financial.setBudget')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          {t('financial.loading')}
        </div>
      ) : !budget ? (
        <div className="text-center py-12">
          <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">
            {t('financial.noBudgetDefined')}
          </p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={openBudgetModal}
          >
            {t('financial.setBudget')}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">{t('financial.totalBudget')}</p>
              <p className="text-xl font-semibold text-foreground data-text">{formatCurrency(totalBudget)}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">{t('financial.actualCosts')}</p>
              <p className="text-xl font-semibold text-foreground data-text">{formatCurrency(totalActual)}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">{t('financial.variance')}</p>
              <p className={`text-xl font-semibold data-text ${getVarianceColor(totalVariance)}`}>
                {formatCurrency(Math.abs(totalVariance))}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">{t('financial.utilization')}</p>
              <p className={`text-xl font-semibold data-text`} style={{ color: getPercentageColor(totalPercentage) }}>
                {totalPercentage?.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="border border-border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-4">
              {t('financial.categoryBreakdown')}
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="category" 
                  tickFormatter={(value) => categoryLabels?.[value] || value}
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => categoryLabels?.[label] || label}
                />
                <Legend 
                  formatter={(value) => value === 'budget' ? t('financial.budget') : t('financial.actual')}
                />
                <Bar dataKey="budget" fill="#3b82f6" name="budget" />
                <Bar dataKey="actual" name="actual">
                  {categoryData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getPercentageColor(entry?.percentage)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Details Table */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{t('financial.category')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">{t('financial.budget')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">{t('financial.actual')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">{t('financial.variance')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">{t('financial.utilization')}</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryData?.map((item, index) => (
                    <tr key={index} className="border-t border-border hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {categoryLabels?.[item?.category]}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground text-right data-text">
                        {formatCurrency(item?.budget)}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground text-right data-text">
                        {formatCurrency(item?.actual)}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium data-text ${getVarianceColor(item?.variance)}`}>
                        {formatCurrency(Math.abs(item?.variance))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium data-text" style={{ color: getPercentageColor(item?.percentage) }}>
                        {item?.percentage?.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-warm-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-xl font-semibold text-foreground">
                {budget ? t('financial.editBudget') : t('financial.setBudget')}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setShowBudgetModal(false)}
              />
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('financial.totalBudget')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  value={budgetForm?.totalBudget}
                  onChange={(e) => setBudgetForm({ ...budgetForm, totalBudget: e?.target?.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.accommodation')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.accommodationBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, accommodationBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.transport')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.transportBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, transportBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.meals')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.mealsBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, mealsBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.guides')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.guidesBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, guidesBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.visasCost')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.visasBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, visasBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('financial.other')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    value={budgetForm?.otherBudget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, otherBudget: e?.target?.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setShowBudgetModal(false)}
                >
                  {t('financial.cancel')}
                </Button>
                <Button
                  variant="default"
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleSaveBudget}
                >
                  {t('financial.saveBudget')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetAnalysisPanel;